/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useCurrentBasket} from '@/hooks/basket/useCurrentBasket'
import {Dialog, DialogContent, DialogDescription} from '@/components/ui/dialog'

import {AddToCartContent} from './AddToCartContent'
import {ProductRecommendations} from 'hec/components/product-recommendations'
import {ScrollArea} from 'hec/components/ui/scroll-area'

/**
 * This is the context for managing the AddToCartModal.
 * Used in top level App component.
 */
export const AddToCartModalContext = React.createContext()
export const useAddToCartModalContext = () => useContext(AddToCartModalContext)
export const AddToCartModalProvider = ({children}) => {
    const addToCartModal = useAddToCartModal()
    return (
        <AddToCartModalContext.Provider value={addToCartModal}>
            {children}
            <AddToCartModal />
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
export const AddToCartModal = () => {
    const {isOpen, onClose, data} = useAddToCartModalContext()
    const {product, quantity, recommendations} = data || {}
    const podId = recommendations?.podId || 'atc-modal'

    const {
        data: basket,
        derivedData: {totalItems}
    } = useCurrentBasket()
    const productIds = basket?.productItems && basket.productItems.map((item) => item.productId)
    const {currency, productSubTotal} = basket || {}
    const selectedProductAdded =
        product?.id &&
        basket?.productItems &&
        basket.productItems.find((item) => item.productId === product.id)

    if (!isOpen) {
        return null
    }

    return (
        <div className="max-w-[30vw]">
            <Dialog open={isOpen} onOpenChange={onClose}>
                <ScrollArea>
                    <DialogContent
                        type="addToCart"
                        className="block fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] max-w-[23.4375rem] md:max-w-[46.875rem] w-full rounded-[6px] max-h-[100vh] overflow-y-auto overflow-x-hidden p-6"
                    >
                        <DialogDescription>
                            <AddToCartContent
                                closeModal={onClose}
                                cartData={{totalItems, productSubTotal}}
                                productData={{
                                    image: product.image,
                                    name: selectedProductAdded?.productName,
                                    basePrice: selectedProductAdded?.basePrice,
                                    quantity: quantity,
                                    currency: currency
                                }}
                            />
                            <div className="mt-[2rem] sm:ml-[1.5rem] xs:ml-[1rem] sm:mr-[1.5rem] xs:mr-[1rem]">
                                <ProductRecommendations
                                    podId={podId}
                                    slidesVisible={{onDesktop: 3}}
                                    isAddToCart={true}
                                    productIds={productIds}
                                    attributes={{className: 'w-[100%] mx-auto mt-3'}}
                                />
                            </div>
                        </DialogDescription>
                    </DialogContent>
                </ScrollArea>
            </Dialog>
        </div>
    )
}

AddToCartModal.propTypes = {
    recommendations: PropTypes.shape({
        podId: PropTypes.string
    }),
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array,
        image: PropTypes.string,
        id: PropTypes.string
    }),
    basket: PropTypes.object,
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any
}

export const useAddToCartModal = () => {
    const [state, setState] = useState({
        isOpen: false,
        data: null
    })

    const {pathname} = useLocation()
    useEffect(() => {
        if (state.isOpen) {
            setState({
                ...state,
                isOpen: false
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    return {
        isOpen: state.isOpen,
        data: state.data,
        onOpen: (data) => {
            setState({
                isOpen: true,
                data
            })
        },
        onClose: () => {
            setState({
                isOpen: false,
                data: null
            })
        }
    }
}
